import { apiClient as axios } from '../lib/authClient';

const API_URL = '/notifications';

/**
 * Get notifications with filters and pagination
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.is_read - Filter: 'true', 'false', 'all' (default: 'all')
 * @param {string} options.type - Filter by type: opportunity, task, customer, etc.
 * @param {string} options.priority - Filter by priority: low, normal, high, urgent
 * @param {string} options.category - Filter by category
 * @param {string} options.sort - Sort field (default: 'created_at')
 * @param {string} options.order - Sort order: 'asc', 'desc' (default: 'desc')
 * @returns {Promise<Object>} Response with notifications, unread_count, and pagination
 */
export const getNotifications = async (options = {}) => {
    try {
        const {
            page = 1,
            limit = 20,
            is_read = 'all',
            type = null,
            priority = null,
            category = null,
            sort = 'created_at',
            order = 'desc'
        } = options;

        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            is_read,
            sort,
            order
        });

        if (type) params.append('type', type);
        if (priority) params.append('priority', priority);
        if (category) params.append('category', category);

        const response = await axios.get(`${API_URL}?${params}`);

        if (response.data.status) {
            return response.data.data;
        } else {
            // Fallback for unwrapped responses
            return response.data.data || response.data;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get unread notification count
 * @returns {Promise<Object>} Unread count with breakdown by type and priority
 */
export const getUnreadCount = async () => {
    try {
        const response = await axios.get(`${API_URL}/unread-count`);

        if (response.data.status) {
            return response.data.data;
        } else {
            // If the structure is different, fallback to the data itself
            return response.data.data || response.data;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Mark a single notification as read
 * @param {number} id - Notification ID
 * @returns {Promise<Object>} Updated notification data
 */
export const markAsRead = async (id) => {
    try {
        const response = await axios.put(`${API_URL}/${id}/read`, {});

        if (response.data.status) {
            return response.data.data;
        } else {
            throw new Error(response.data.message || 'Failed to mark as read');
        }
    } catch (error) {
        console.error('Error marking notification as read:', error);
        throw error;
    }
};

/**
 * Mark all notifications as read
 * @param {string} type - Optional: mark only specific type
 * @returns {Promise<Object>} Updated count
 */
export const markAllAsRead = async (type = null) => {
    try {
        const url = type
            ? `${API_URL}/mark-all-read?type=${type}`
            : `${API_URL}/mark-all-read`;

        const response = await axios.put(url, {});

        if (response.data.status) {
            return response.data.data;
        } else {
            throw new Error(response.data.message || 'Failed to mark all as read');
        }
    } catch (error) {
        console.error('Error marking all as read:', error);
        throw error;
    }
};

/**
 * Delete a notification
 * @param {number} id - Notification ID
 * @returns {Promise<Object>} Success response
 */
export const deleteNotification = async (id) => {
    try {
        const response = await axios.delete(`${API_URL}/${id}`);

        if (response.data.status) {
            return response.data;
        } else {
            throw new Error(response.data.message || 'Failed to delete notification');
        }
    } catch (error) {
        console.error('Error deleting notification:', error);
        throw error;
    }
};

/**
 * Clear all read notifications
 * @returns {Promise<Object>} Deleted count
 */
export const clearReadNotifications = async () => {
    try {
        const response = await axios.delete(`${API_URL}/clear-read`);

        if (response.data.status) {
            return response.data.data;
        } else {
            throw new Error(response.data.message || 'Failed to clear read notifications');
        }
    } catch (error) {
        console.error('Error clearing read notifications:', error);
        throw error;
    }
};

/**
 * Get notification preferences
 * @returns {Promise<Array>} List of preferences
 */
export const getPreferences = async () => {
    try {
        const response = await axios.get(`${API_URL}/preferences`);

        if (response.data.status) {
            return response.data.data.preferences;
        } else {
            throw new Error(response.data.message || 'Failed to fetch preferences');
        }
    } catch (error) {
        console.error('Error fetching preferences:', error);
        throw error;
    }
};

/**
 * Update notification preferences
 * @param {Array} preferences - Array of preference objects
 * @returns {Promise<Object>} Updated count
 */
export const updatePreferences = async (preferences) => {
    try {
        const response = await axios.put(`${API_URL}/preferences`, {
            preferences
        });

        if (response.data.status) {
            return response.data.data;
        } else {
            throw new Error(response.data.message || 'Failed to update preferences');
        }
    } catch (error) {
        console.error('Error updating preferences:', error);
        throw error;
    }
};



// Export all functions as default object for convenience
export default {
    getNotifications,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearReadNotifications,
    getPreferences,
    updatePreferences
};
